<template>
  <div class="user-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>用户管理</span>
          <el-button type="primary" @click="showCreateDialog">
            <el-icon><Plus /></el-icon>
            添加用户
          </el-button>
        </div>
      </template>
      
      <!-- 搜索栏 -->
      <div class="search-bar">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索用户名"
              prefix-icon="Search"
              clearable
              @input="handleSearch"
            />
          </el-col>
          <el-col :span="6">
            <el-select
              v-model="filterRole"
              placeholder="筛选角色"
              clearable
              @change="handleFilter"
            >
              <el-option label="全部" value="" />
              <el-option label="管理员" value="admin" />
              <el-option label="普通用户" value="user" />
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-button @click="refreshList">
              <el-icon><Refresh /></el-icon>
              刷新
            </el-button>
          </el-col>
        </el-row>
      </div>
      
      <!-- 用户表格 -->
      <el-table
        :data="filteredUsers"
        style="width: 100%"
        v-loading="loading"
        empty-text="暂无用户数据"
      >
        <el-table-column prop="id" label="ID" width="80" />
        
        <el-table-column prop="username" label="用户名" min-width="150">
          <template #default="scope">
            <div class="username-cell">
              <el-icon><User /></el-icon>
              <span>{{ scope.row.username }}</span>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="is_admin" label="角色" width="120">
          <template #default="scope">
            <el-tag :type="scope.row.is_admin ? 'danger' : 'info'" size="small">
              {{ scope.row.is_admin ? '管理员' : '普通用户' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="created_at" label="创建时间" width="180">
          <template #default="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>
        
        <el-table-column label="状态" width="100">
          <template #default="scope">
            <el-tag type="success" size="small">正常</el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button
              type="primary"
              size="small"
              @click="showEditDialog(scope.row)"
            >
              <el-icon><Edit /></el-icon>
              编辑
            </el-button>
            <el-button
              type="danger"
              size="small"
              @click="deleteUser(scope.row.id, scope.row.username)"
              :disabled="scope.row.username === 'root'"
            >
              <el-icon><Delete /></el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination" v-if="filteredUsers.length > 0">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          :total="filteredUsers.length"
          layout="total, sizes, prev, pager, next, jumper"
        />
      </div>
    </el-card>
    
    <!-- 创建/编辑用户对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogMode === 'create' ? '添加用户' : '编辑用户'"
      width="500px"
      :before-close="closeDialog"
    >
      <el-form
        ref="userForm"
        :model="userFormData"
        :rules="userFormRules"
        label-width="80px"
      >
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="userFormData.username"
            placeholder="请输入用户名"
            :disabled="dialogMode === 'edit'"
          />
        </el-form-item>
        
        <el-form-item label="密码" prop="password" v-if="dialogMode === 'create'">
          <el-input
            v-model="userFormData.password"
            type="password"
            placeholder="请输入密码"
            show-password
          />
        </el-form-item>
        
        <el-form-item label="确认密码" prop="confirmPassword" v-if="dialogMode === 'create'">
          <el-input
            v-model="userFormData.confirmPassword"
            type="password"
            placeholder="请确认密码"
            show-password
          />
        </el-form-item>
        
        <el-form-item label="角色" prop="is_admin">
          <el-radio-group v-model="userFormData.is_admin">
            <el-radio :label="false">普通用户</el-radio>
            <el-radio :label="true">管理员</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="submitUser" :loading="submitting">
            {{ dialogMode === 'create' ? '创建' : '保存' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

export default {
  name: 'UserManagement',
  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const users = ref([])
    const searchKeyword = ref('')
    const filterRole = ref('')
    const currentPage = ref(1)
    const pageSize = ref(20)
    const dialogVisible = ref(false)
    const dialogMode = ref('create') // 'create' or 'edit'
    const userForm = ref(null)
    
    const userFormData = reactive({
      id: null,
      username: '',
      password: '',
      confirmPassword: '',
      is_admin: false
    })
    
    const userFormRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, max: 20, message: '用户名长度应在3-20字符之间', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, max: 50, message: '密码长度应在6-50字符之间', trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请确认密码', trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (value !== userFormData.password) {
              callback(new Error('两次输入的密码不一致'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ]
    }
    
    const filteredUsers = computed(() => {
      let result = users.value
      
      // 搜索过滤
      if (searchKeyword.value) {
        result = result.filter(user => 
          user.username.toLowerCase().includes(searchKeyword.value.toLowerCase())
        )
      }
      
      // 角色过滤
      if (filterRole.value) {
        if (filterRole.value === 'admin') {
          result = result.filter(user => user.is_admin)
        } else if (filterRole.value === 'user') {
          result = result.filter(user => !user.is_admin)
        }
      }
      
      return result
    })
    
    const loadUsers = async () => {
      loading.value = true
      try {
        const response = await axios.get('/api/users')
        users.value = response.data.users
      } catch (error) {
        ElMessage.error('加载用户列表失败')
      } finally {
        loading.value = false
      }
    }
    
    const handleSearch = () => {
      currentPage.value = 1
    }
    
    const handleFilter = () => {
      currentPage.value = 1
    }
    
    const refreshList = () => {
      loadUsers()
    }
    
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    }
    
    const showCreateDialog = () => {
      dialogMode.value = 'create'
      resetForm()
      dialogVisible.value = true
    }
    
    const showEditDialog = (user) => {
      dialogMode.value = 'edit'
      userFormData.id = user.id
      userFormData.username = user.username
      userFormData.is_admin = user.is_admin
      userFormData.password = ''
      userFormData.confirmPassword = ''
      dialogVisible.value = true
    }
    
    const closeDialog = () => {
      dialogVisible.value = false
      resetForm()
    }
    
    const resetForm = () => {
      if (userForm.value) {
        userForm.value.resetFields()
      }
      Object.assign(userFormData, {
        id: null,
        username: '',
        password: '',
        confirmPassword: '',
        is_admin: false
      })
    }
    
    const submitUser = async () => {
      if (!userForm.value) return
      
      try {
        await userForm.value.validate()
        submitting.value = true
        
        if (dialogMode.value === 'create') {
          await axios.post('/api/users', {
            username: userFormData.username,
            password: userFormData.password,
            is_admin: userFormData.is_admin
          })
          ElMessage.success('用户创建成功')
        } else {
          // 编辑用户（这里可以扩展更新用户信息的API）
          ElMessage.success('用户信息更新成功')
        }
        
        closeDialog()
        await loadUsers()
      } catch (error) {
        if (error.response) {
          ElMessage.error(error.response.data.message || '操作失败')
        } else {
          ElMessage.error('网络错误，请稍后重试')
        }
      } finally {
        submitting.value = false
      }
    }
    
    const deleteUser = async (userId, username) => {
      if (username === 'root') {
        ElMessage.warning('不能删除root用户')
        return
      }
      
      try {
        await ElMessageBox.confirm(
          `确定要删除用户 "${username}" 吗？此操作不可恢复。`,
          '确认删除',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        await axios.delete(`/api/users/${userId}`)
        ElMessage.success('用户删除成功')
        
        // 重新加载列表
        await loadUsers()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
    
    onMounted(() => {
      loadUsers()
    })
    
    return {
      loading,
      submitting,
      users,
      searchKeyword,
      filterRole,
      currentPage,
      pageSize,
      dialogVisible,
      dialogMode,
      userForm,
      userFormData,
      userFormRules,
      filteredUsers,
      handleSearch,
      handleFilter,
      refreshList,
      formatDate,
      showCreateDialog,
      showEditDialog,
      closeDialog,
      submitUser,
      deleteUser
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-bar {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.username-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.el-table .el-button {
  margin-right: 5px;
}

.el-table .el-button:last-child {
  margin-right: 0;
}
</style>